import React from 'react';
import { Calculator, Building2, Factory, Zap } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров трансформации
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating,
  transformationTypes,
  companySizes
}) => {
  // Парсинг числовых значений из формы
  const projectDuration = parseInt(formData.projectDuration, 10) || 12;
  const internalTeamSize = parseInt(formData.internalTeamSize, 10) || 5;
  const averageSalary = parseInt(formData.averageSalary, 10) || 60000;
  
  // Обработчик изменения продолжительности проекта
  const handleProjectDurationChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      projectDuration: newValue
    }));
  };
  
  // Обработчик изменения размера внутренней команды
  const handleInternalTeamSizeChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      internalTeamSize: newValue
    }));
  };
  
  // Обработчик изменения средней зарплаты
  const handleAverageSalaryChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      averageSalary: newValue
    }));
  };
  
  // Обработчик нажатия на кнопку типа трансформации
  const handleTransformationTypeClick = (typeId) => {
    setFormData(prev => ({
      ...prev,
      transformationType: typeId
    }));
  };
  
  // Обработчик нажатия на кнопку размера компании
  const handleCompanySizeClick = (sizeId) => {
    setFormData(prev => ({
      ...prev,
      companySize: sizeId
    }));
  };
  
  // Функция расчета
  const calculateCost = () => {
    // Здесь должна быть вызвана функция основного расчета
    // В данном случае просто эмулируем действие, так как основная логика 
    // находится в родительском компоненте
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-indigo-500" />
        Быстрая настройка параметров
      </h3>
      
      <div className="space-y-5">
        {/* Размер компании */}
        <div>
          <div className="flex items-center mb-2 text-sm">
            <Building2 className="w-4 h-4 mr-2 text-indigo-500" />
            <label className="text-gray-600 dark:text-gray-400">Размер компании</label>
          </div>
          <div className="grid grid-cols-2 gap-1">
            {companySizes.map(size => (
              <button
                key={size.id}
                type="button"
                onClick={() => handleCompanySizeClick(size.id)}
                className={`px-2 py-2 text-xs rounded border transition-colors
                  ${formData.companySize === size.id 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                <div className="font-medium">{size.name}</div>
                <div className="text-xs opacity-75">{size.description}</div>
              </button>
            ))}
          </div>
        </div>
        
        {/* Тип трансформации */}
        <div>
          <div className="flex items-center mb-2 text-sm">
            <Zap className="w-4 h-4 mr-2 text-indigo-500" />
            <label className="text-gray-600 dark:text-gray-400">Тип трансформации</label>
          </div>
          <div className="grid grid-cols-1 gap-1">
            {transformationTypes.map(type => (
              <button
                key={type.id}
                type="button"
                onClick={() => handleTransformationTypeClick(type.id)}
                className={`px-2 py-2 text-xs rounded border transition-colors
                  ${formData.transformationType === type.id 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                <div className="font-medium">{type.name}</div>
                <div className="text-xs opacity-75">{type.description}</div>
              </button>
            ))}
          </div>
        </div>
        
        {/* Продолжительность проекта */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="projectDurationSlider" className="text-gray-600 dark:text-gray-400">Продолжительность (мес.)</label>
            <span className="font-medium text-gray-900 dark:text-white">{projectDuration} мес.</span>
          </div>
          <input
            type="range"
            id="projectDurationSlider"
            min="3"
            max="36"
            step="1"
            value={projectDuration}
            onChange={handleProjectDurationChange}
            aria-label="Продолжительность проекта (месяцев)"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Предопределенные продолжительности */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {[3, 6, 12, 24].map(duration => (
              <button
                key={duration}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, projectDuration: duration.toString() }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${projectDuration === duration 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {duration} мес.
              </button>
            ))}
          </div>
        </div>
        
        {/* Размер внутренней команды */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="internalTeamSizeSlider" className="text-gray-600 dark:text-gray-400">Размер команды</label>
            <span className="font-medium text-gray-900 dark:text-white">{internalTeamSize} чел.</span>
          </div>
          <input
            type="range"
            id="internalTeamSizeSlider"
            min="1"
            max="20"
            step="1"
            value={internalTeamSize}
            onChange={handleInternalTeamSizeChange}
            aria-label="Размер внутренней команды (человек)"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Предопределенные размеры команды */}
          <div className="grid grid-cols-5 gap-1 mt-2">
            {[1, 3, 5, 10, 15].map(size => (
              <button
                key={size}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, internalTeamSize: size.toString() }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${internalTeamSize === size 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {size}
              </button>
            ))}
          </div>
        </div>
        
        {/* Средняя зарплата */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="averageSalarySlider" className="text-gray-600 dark:text-gray-400">Средняя зарплата</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(averageSalary, 0)}</span>
          </div>
          <input
            type="range"
            id="averageSalarySlider"
            min="30000"
            max="200000"
            step="5000"
            value={averageSalary}
            onChange={handleAverageSalaryChange}
            aria-label="Средняя зарплата (руб.)"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Предопределенные зарплаты */}
          <div className="grid grid-cols-3 gap-1 mt-2">
            {[40000, 80000, 150000].map(salary => (
              <button
                key={salary}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, averageSalary: salary.toString() }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${Math.abs(averageSalary - salary) < 100
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {formatCurrency(salary, 0)}
              </button>
            ))}
          </div>
        </div>
        
        {/* Кнопка расчета */}
        <Button
          primary
          fullWidth
          onClick={calculateCost}
          icon={<Calculator className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать стоимость
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;